import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import { authGet, authPost, authPut, authPatch } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 900, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  filterInput: { width: 260 },
  nameCell: { width: "55%" },
  statusCell: { width: 110 },
  editCell: { width: 56, padding: "0 4px" },
  toggleCell: { width: 120, padding: "0 4px" },
  emptyCell: { color: "#999", padding: theme.spacing(3), textAlign: "center" },
  active: { color: "#2e7d32", fontWeight: 600 },
  inactive: { color: "#c62828", fontWeight: 600 },
  toggleBtn: {
    padding: "1px 8px",
    minHeight: 0,
    lineHeight: 1.4,
  },
  turnOn: {
    color: "#2e7d32",
    borderColor: "#2e7d32",
  },
  turnOff: {
    color: "#c62828",
    borderColor: "#c62828",
  },
}));

export function TradeInBrandsPage() {
  const classes = useStyles();

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    fetchBrands();
  }, []);

  async function fetchBrands() {
    setLoading(true);
    const { data, error } = await authGet("/trade-in-brands");
    setLoading(false);
    if (error) { showSnack(error?.message || "Failed to load brands"); return; }
    setBrands(data || []);
  }

  function showSnack(msg) {
    setSnack({ open: true, msg });
  }

  function sortByName(list) {
    return [...list].sort((a, b) => a.brand_name.localeCompare(b.brand_name));
  }

  async function handleAdd() {
    const brand_name = newName.trim();
    if (!brand_name) return;
    setAdding(true);
    const { data, error } = await authPost("/trade-in-brands", { brand_name });
    setAdding(false);
    if (error) { showSnack(error?.message || "Failed to add brand"); return; }
    setBrands((prev) => sortByName([...prev, data]));
    setNewName("");
    showSnack("Brand added");
  }

  function startEdit(brand) {
    setEditId(brand.brand_id);
    setEditName(brand.brand_name);
  }

  function cancelEdit() {
    setEditId(null);
  }

  async function handleSaveEdit() {
    const brand_name = editName.trim();
    if (!brand_name) return;
    const { data, error } = await authPut(`/trade-in-brands/${editId}`, { brand_name });
    if (error) { showSnack(error?.message || "Failed to update brand"); return; }
    setBrands((prev) =>
      sortByName(prev.map((b) => (b.brand_id === editId ? { ...b, brand_name: data.brand_name } : b)))
    );
    setEditId(null);
    showSnack("Brand updated");
  }

  async function handleToggle(brand) {
    const is_active = brand.is_active ? false : true;
    const { error } = await authPatch(`/trade-in-brands/${brand.brand_id}`, { is_active });
    if (error) { showSnack(error?.message || "Failed to toggle brand"); return; }
    setBrands((prev) =>
      prev.map((b) => (b.brand_id === brand.brand_id ? { ...b, is_active: is_active ? 1 : 0 } : b))
    );
    showSnack(is_active ? "Brand activated" : "Brand deactivated");
  }

  const filterLower = filter.toLowerCase();
  const visible = filter
    ? brands.filter((b) => b.brand_name.toLowerCase().includes(filterLower))
    : brands;

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>Trade-In Brands</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Manage the brands eligible for trade-in submissions. Inactive brands are kept on file but hidden from customers.
      </Typography>

      <Box className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter brands…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={classes.filterInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary">
          {visible.length} of {brands.length} brand{brands.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.nameCell}>Brand Name</TableCell>
              <TableCell className={classes.statusCell}>Status</TableCell>
              <TableCell className={classes.editCell} />
              <TableCell className={classes.toggleCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="New brand name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </TableCell>
              <TableCell colSpan={3}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={adding || !newName.trim()}
                  onClick={handleAdd}
                >
                  Add Brand
                </Button>
              </TableCell>
            </TableRow>

            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyCell}>
                  {filter ? "No matching brands" : "No brands yet"}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((b) => (
                <TableRow key={b.brand_id} hover>
                  <TableCell>
                    {editId === b.brand_id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                      />
                    ) : (
                      b.brand_name
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={b.is_active ? classes.active : classes.inactive}>
                      {b.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className={classes.editCell} align="center">
                    {editId === b.brand_id ? (
                      <>
                        <IconButton size="small" onClick={handleSaveEdit} title="Save">
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={cancelEdit} title="Cancel">
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton size="small" onClick={() => startEdit(b)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell className={classes.toggleCell} align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      className={`${classes.toggleBtn} ${b.is_active ? classes.turnOff : classes.turnOn}`}
                      onClick={() => handleToggle(b)}
                    >
                      {b.is_active ? "Turn OFF" : "Turn ON"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}
